import { clockIcon } from '@/assets';
import ozonMarketplaceIcon from '@/assets/icons/ozon-marketplace.svg';
import wbMarketplaceIcon from '@/assets/icons/wb-marketplace.svg';
import ProductCell from '@/components/ProductCell';
import { FeedbacksTableData } from '@/components/feedbacks-table/feedbacks-table-columns.types';
import { Marketplace } from '@/enums/marketplace';
import { SortDirection } from '@/enums/sort';
import type { ReviewEntity, ReviewPhotoEntity } from '@/entities/review.entity';
import Rating from '@/ui/Rating';
import { TableColumn } from '@/ui/Table';
import { formatDateTimePartsInGmtPlus3 } from '@/shared/date-time-gmt-plus-3';
import { normalizeProductSizeForDisplay } from '@/shared/format-product-dropdown-info';
import { formatReviewTableTextForDisplay } from '@/shared/format-review-table-text-for-display';
import type { RuntimeSupportedMarketplace } from '@/shared/marketplace';
import { getAutoReplyProcessingResultLabel, getReviewAnswerDraftStatusLabel } from '@/shared/auto-replies-labels';
import { resolveReviewAiReviewTypeLabel } from '@/shared/review-ai-review-types';
import { getReviewStatusFilterLabel, resolveReviewStatusDisplayValue } from '@/shared/review-status-filter';
import Image from 'next/image';
import type { MouseEvent } from 'react';

type BuildFeedbacksTableColumnsArgs = {
    getAnswerJob: (reviewId: string) => { data: { text?: string } } | undefined;
    onPhotoPreviewOpen: (event: MouseEvent<HTMLButtonElement>, photos: ReviewPhotoEntity[], index: number) => void;
    onAiResponseOpen: (event: MouseEvent<HTMLButtonElement>, review: ReviewEntity) => void;
};

const platformLabelByValue: Record<RuntimeSupportedMarketplace, string> = {
    [Marketplace.Wb]: 'WB',
    [Marketplace.Ozon]: 'Ozon',
};

const platformIconByValue: Record<RuntimeSupportedMarketplace, string> = {
    [Marketplace.Wb]: wbMarketplaceIcon,
    [Marketplace.Ozon]: ozonMarketplaceIcon,
};

export const getPlatformLabel = (platform: Marketplace | undefined): string | undefined => {
    if (platform === Marketplace.Wb || platform === Marketplace.Ozon) {
        return platformLabelByValue[platform];
    }

    return undefined;
};

const getPlatformIcon = (platform: Marketplace | undefined): string | undefined => {
    if (platform === Marketplace.Wb || platform === Marketplace.Ozon) {
        return platformIconByValue[platform];
    }

    return undefined;
};

const getReviewClassLabel = (review: ReviewEntity): string | undefined => {
    const reviewClass = review.aiApiResponse?.review?.review_class;
    if (typeof reviewClass !== 'string') {
        return undefined;
    }

    const normalizedReviewClass = reviewClass.trim();
    return resolveReviewAiReviewTypeLabel(normalizedReviewClass) ?? undefined;
};

const getReviewAnswerLocalStatusLabel = (review: ReviewEntity): string | null => {
    const draftStatus = review.pendingAnswerDraft?.status;
    if (draftStatus) {
        return getReviewAnswerDraftStatusLabel(draftStatus);
    }

    if (review.autoReplyProcessing?.result === 'processing') {
        return getAutoReplyProcessingResultLabel(review.autoReplyProcessing.result);
    }

    return null;
};

const renderAnswerLocalStatusBadge = (label: string) => (
    <span className="inline-flex rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800">
        {label}
    </span>
);

export const buildFeedbacksTableColumns = ({
    getAnswerJob,
    onPhotoPreviewOpen,
    onAiResponseOpen,
}: BuildFeedbacksTableColumnsArgs): TableColumn<FeedbacksTableData>[] => [
    {
        key: 'createdDate',
        alias: 'Создан',
        sort: SortDirection.NONE,
        className:
            'w-[1%] min-w-[74px] md:min-w-[82px] lg:whitespace-nowrap max-w-[40vw] md:max-w-[24vw] lg:max-w-none',
        render: (date, record) => {
            if (!date) return;

            const formattedDateTime = formatDateTimePartsInGmtPlus3(date.toString());
            if (!formattedDateTime) return date.toString();
            const platformIcon = getPlatformIcon((record as ReviewEntity).platform);

            return (
                <div className="flex flex-col items-start text-[13px] leading-[1.15] text-slate-900">
                    <span className="text-[80%] whitespace-nowrap">{formattedDateTime.date}</span>
                    <span className="mt-0.5 flex items-center gap-1 whitespace-nowrap">
                        {platformIcon && (
                            <Image
                                src={platformIcon}
                                alt=""
                                aria-hidden="true"
                                width={16}
                                height={16}
                                className="h-4 w-4 shrink-0"
                            />
                        )}
                        <span className="leading-none">{formattedDateTime.time}</span>
                    </span>
                </div>
            );
        },
    },
    {
        key: 'rating',
        sort: SortDirection.NONE,
        alias: 'Рейт.',
        render: (value) => <Rating value={Number(value) || 0} />,
        className: 'w-[1%] min-w-[78px] lg:whitespace-nowrap',
    },
    {
        key: 'text',
        alias: 'Отзыв',
        render: (_, record) => {
            const review = record as ReviewEntity;
            const preparedReview = formatReviewTableTextForDisplay(review);
            const reviewPhotos = review.photos || [];
            if (!preparedReview && !reviewPhotos.length) return;

            return (
                <div className="flex min-w-0 items-center gap-2">
                    {reviewPhotos.length > 0 && (
                        <div className="flex shrink-0 items-center gap-1 overflow-x-auto">
                            {reviewPhotos.map((photo, index) => (
                                <button
                                    type="button"
                                    key={`${review.id}-photo-inline-${index}`}
                                    aria-label={`Открыть фото ${index + 1}`}
                                    className="h-[30px] w-[30px] overflow-hidden rounded border border-slate-200 transition-colors hover:border-slate-500"
                                    onClick={(event) => onPhotoPreviewOpen(event, reviewPhotos, index)}
                                >
                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                    <img
                                        src={photo.miniSize}
                                        alt={`Фото ${index + 1}`}
                                        className="h-full w-full object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                    {preparedReview && <div className="line-clamp-2 min-w-0 break-words">{preparedReview}</div>}
                </div>
            );
        },
        className: 'min-w-[200px] md:min-w-[275px] max-w-[70vw] md:max-w-[42vw] lg:max-w-100',
    },
    {
        key: 'answer',
        alias: 'Ответ',
        render: (_, record) => {
            const review = record as ReviewEntity;
            const answerJob = getAnswerJob(review.id);
            const localStatusLabel = getReviewAnswerLocalStatusLabel(review);

            const preparedAnswerText = (answerJob?.data.text || review.answer?.text)?.replaceAll('\\n', '\n');
            if (!preparedAnswerText) {
                if (!localStatusLabel) return;

                return renderAnswerLocalStatusBadge(localStatusLabel);
            }

            return (
                <div className="line-clamp-2 break-words">
                    <p>{preparedAnswerText}</p>
                    {!answerJob && localStatusLabel && (
                        <p className="mt-1 text-xs font-medium text-amber-700">{localStatusLabel}</p>
                    )}
                    {answerJob && (
                        <div className="flex items-center gap-[5px]">
                            <Image src={clockIcon} alt={''} width={20} />
                            <p className="text-gray-500 text-sm">В очереди</p>
                        </div>
                    )}
                </div>
            );
        },
        className: 'min-w-[112px] md:min-w-[154px] lg:min-w-[220px] max-w-[49vw] md:max-w-[30vw] lg:max-w-100',
    },
    {
        key: 'convertedStatus',
        sort: SortDirection.NONE,
        alias: 'Статус',
        className:
            'w-[1%] min-w-[72px] md:min-w-[82px] lg:whitespace-nowrap max-w-[28vw] md:max-w-[20vw] lg:max-w-none',
        render: (_, record) => {
            const review = record as ReviewEntity;
            const status = resolveReviewStatusDisplayValue({
                convertedStatus: review.convertedStatus,
                pendingAnswerDraft: review.pendingAnswerDraft,
                autoReplyProcessing: review.autoReplyProcessing,
            });

            return getReviewStatusFilterLabel(status);
        },
    },
    {
        key: 'product:name',
        alias: 'Продукт',
        sort: SortDirection.NONE,
        render: (_, record) => {
            const review = record as ReviewEntity;
            const productPhotos = review.product.photos || [];
            const firstProductPhoto = productPhotos[0];

            return (
                <div className="flex min-w-0 items-center gap-2">
                    {firstProductPhoto && (
                        <div className="flex shrink-0 items-center gap-1 overflow-x-auto">
                            <button
                                type="button"
                                key={`${review.id}-product-photo-inline-0`}
                                aria-label="Открыть фото продукта 1"
                                className="h-[30px] w-[30px] overflow-hidden rounded border border-slate-200 transition-colors hover:border-slate-500"
                                onClick={(event) => onPhotoPreviewOpen(event, productPhotos, 0)}
                            >
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src={firstProductPhoto.miniSize}
                                    alt="Фото продукта 1"
                                    className="h-full w-full object-cover"
                                />
                            </button>
                        </div>
                    )}
                    <ProductCell product={review.product} />
                </div>
            );
        },
        className: 'min-w-[105px] md:min-w-[147px] lg:min-w-[210px] max-w-[43vw] md:max-w-[25vw] lg:max-w-none',
    },
    {
        key: 'size',
        alias: 'Р-р',
        className: 'min-w-[64px] md:min-w-[80px]',
        render: (_, record) => {
            const size = normalizeProductSizeForDisplay((record as ReviewEntity).product.size);

            return <div className="max-w-[24vw] md:max-w-[16vw] lg:max-w-16">{size}</div>;
        },
    },
    {
        key: 'product:brandName',
        alias: 'Бренд',
        sort: SortDirection.NONE,
        className: 'min-w-[110px] md:min-w-[140px]',
        render: (_, record) => {
            const brandName = (record as ReviewEntity).product.brandName || '';
            return (
                <div
                    className="w-full min-w-0 line-clamp-2 leading-tight break-words whitespace-normal"
                    title={brandName}
                >
                    {brandName}
                </div>
            );
        },
    },
    {
        key: 'platform',
        sort: SortDirection.NONE,
        alias: 'МП',
        className: 'w-[1%] min-w-[50px] lg:whitespace-nowrap',
        render: (value) => {
            const platform = value as Marketplace | undefined;
            return getPlatformLabel(platform);
        },
    },
    {
        key: 'answer:updatedDate',
        alias: 'Отвечен',
        sort: SortDirection.NONE,
        className: 'min-w-[86px] md:min-w-[94px] max-w-[42vw] md:max-w-[26vw] lg:max-w-none',
        render: (_, record) => {
            const updatedDate = record.answer?.updatedDate;
            if (!updatedDate) return;

            const formattedDateTime = formatDateTimePartsInGmtPlus3(updatedDate.toString());
            if (!formattedDateTime) return updatedDate.toString();

            return (
                <div className="flex flex-col leading-tight">
                    <span className="text-[80%]">{formattedDateTime.date}</span>
                    <span>{formattedDateTime.time}</span>
                </div>
            );
        },
    },
    {
        key: 'review:review_class',
        alias: 'Тип',
        className: 'min-w-[86px] md:min-w-[96px] max-w-[30vw] md:max-w-[22vw] lg:max-w-[150px]',
        render: (_, record) => {
            const reviewClass = getReviewClassLabel(record as ReviewEntity);
            if (!reviewClass) return;

            return (
                <div className="line-clamp-2 leading-tight break-words whitespace-normal" title={reviewClass}>
                    {reviewClass}
                </div>
            );
        },
    },
    {
        key: 'aiApiResponse',
        alias: 'AI',
        sort: SortDirection.NONE,
        className: 'w-[1%] min-w-[44px] lg:whitespace-nowrap',
        render: (_, record) => {
            const review = record as ReviewEntity;

            if (!review.aiApiResponse) return;

            return (
                <button
                    type="button"
                    aria-label="Открыть ИИ-анализ"
                    className="flex h-7 min-w-7 items-center justify-center rounded border border-blue-300 bg-blue-50 px-1 text-[11px] font-semibold leading-none tracking-wide text-blue-700 transition-colors hover:bg-blue-100"
                    onClick={(event) => onAiResponseOpen(event, review)}
                >
                    AI
                </button>
            );
        },
    },
];
